import { createRoute, useNavigate } from "@tanstack/solid-router";
import {
  BIBLE_ANNOTATION_KIND,
  type BibleAnnotationExportRow,
  buildBibleAnnotationsDataset,
  exportDataset,
  type ExportFormat,
  generateId,
  queryKeys,
  restoreNoteMutation,
  softDeleteNoteMutation,
  updateNoteContentMutation,
} from "@bso/shared";
import { EXPORT_DELIVERY } from "@fleet/plugins/export";
import { useKernelService } from "@fleet/kernel";
import { appRoute } from "./route";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import {
  BookOpen,
  Calendar,
  Check,
  ChevronDown,
  Download,
  Edit3,
  FileText,
  Filter,
  Layers,
  Plus,
  Tag,
  Trash2,
  X,
} from "@fleet/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { bibleBooks, bookGroups, getBookNameDescriptor } from "@/utils/bible-books-data";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import {
  applyVerseGroupFilters,
  compareByCanonicalOrder,
  compareByDate,
  compareVerseRefs,
} from "@/utils/verse-collections";
import { formatVerseReference } from "@/utils/verse-reference";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { Select } from "@/components/ui/select";
import { EmptyIllustration } from "@/components/ui/empty-illustration";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { registerUndo } from "@/lib/undo";
import { createPowerSyncQuery } from "@bso/shared/solid";
import {
  and,
  bible_note,
  bible_note_tag,
  bible_note_verse,
  bible_verse_highlight,
  eq,
  isNull,
} from "@bso/powersync";

type NotesSearch = {
  order?: "DATE" | "CANONICAL";
  b?: number;
  cat?: string;
  tag?: string;
  /**
   * ID of the note open in the editing drawer (`?edit=<noteId>`). Reopenable
   * by URL: it is private data, so a third party sees the drawer closed (nonexistent note).
   */
  edit?: string;
  /** `note` opens the **creation** editor for notes (`?compose=note`). */
  compose?: "note";
};

interface GroupedNote {
  id: string;
  textContent: string;
  color: string;
  createdAt: string;
  tags: string[];
  verses: {
    id: string;
    version: string;
    book: number;
    chapter: number;
    verse: number;
  }[];
}

const PRESET_COLORS = [
  { name: msg`Yellow`, hex: "#eab308" },
  { name: msg`Green`, hex: "#22c55e" },
  { name: msg`Blue`, hex: "#3b82f6" },
  { name: msg`Pink`, hex: "#ec4899" },
  { name: msg`Purple`, hex: "#a855f7" },
];

export const notesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/notes",
  validateSearch: (search: Record<string, unknown>): NotesSearch => {
    const { order } = search;
    return {
      order: order === "DATE" || order === "CANONICAL" ? order : "DATE",
      b: search.b ? Number(search.b) : undefined,
      cat: typeof search.cat === "string" && search.cat ? search.cat : undefined,
      tag: typeof search.tag === "string" && search.tag ? search.tag : undefined,
      edit: typeof search.edit === "string" && search.edit ? search.edit : undefined,
      compose: search.compose === "note" ? "note" : undefined,
    };
  },
  component: NotesPage,
});

function NotesPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const navigate = useNavigate();

  const searchParams = notesRoute.useSearch();
  const orderParam = () => searchParams().order ?? "DATE";
  const bookFilter = () => searchParams().b;
  const categoryFilter = () => searchParams().cat;
  const tagFilter = () => searchParams().tag;
  const editId = () => searchParams().edit;
  const composeParam = () => searchParams().compose;

  const [showBookDropdown, setShowBookDropdown] = createSignal(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = createSignal(false);

  // Selected note for editing/creation
  const [editingNote, setEditingNote] = createSignal<GroupedNote | null>(null);

  // Form states for the editor drawer
  const [editorText, setEditorText] = createSignal("");
  const [editorColor, setEditorColor] = createSignal(PRESET_COLORS[0]?.hex ?? "#eab308");
  const [editorTags, setEditorTags] = createSignal("");
  const [linkedVerses, setLinkedVerses] = createSignal<
    { version: string; book: number; chapter: number; verse: number }[]
  >([]);

  // Picker states for linking a new verse
  const [pickerBook, setPickerBook] = createSignal(1);
  const [pickerChapter, setPickerChapter] = createSignal(1);
  const [pickerVerse, setPickerVerse] = createSignal(1);

  // PowerSync reactive query for notes list
  const notesQuery = createPowerSyncQuery({
    queryKey: () => queryKeys.notes.mine(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }

      return dbInstance
        .select({
          noteId: bible_note.id,
          textContent: bible_note.text_content,
          color: bible_note.color,
          createdAt: bible_note.created_at,
          verseId: bible_note_verse.id,
          version: bible_note_verse.version,
          book: bible_note_verse.book,
          chapter: bible_note_verse.chapter,
          verse: bible_note_verse.verse,
        })
        .from(bible_note)
        .leftJoin(bible_note_verse, eq(bible_note_verse.note_id, bible_note.id))
        .where(and(eq(bible_note.user_id, u), isNull(bible_note.deleted_at)));
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(session.userId()),
  });

  // Tags of the user's notes
  const tagsQuery = createPowerSyncQuery({
    queryKey: () => queryKeys.notes.tags(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({
          noteId: bible_note_tag.note_id,
          tag: bible_note_tag.tag,
        })
        .from(bible_note_tag)
        .innerJoin(bible_note, eq(bible_note.id, bible_note_tag.note_id))
        .where(and(eq(bible_note.user_id, u), isNull(bible_note.deleted_at)));
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(session.userId()),
  });

  const tagsByNote = createMemo(() => {
    const map = new Map<string, string[]>();
    for (const row of tagsQuery.data ?? []) {
      if (!row.noteId || !row.tag) {
        continue;
      }
      const list = map.get(row.noteId) ?? [];
      list.push(row.tag);
      map.set(row.noteId, list);
    }
    return map;
  });

  const allTags = createMemo(() =>
    [...new Set((tagsQuery.data ?? []).flatMap((r) => (r.tag ? [r.tag] : [])))].toSorted((a, b) =>
      a.localeCompare(b),
    ),
  );

  // Group notes list by note id, then apply filters and ordering
  const groupedNotes = createMemo(() => {
    const rows = notesQuery.data ?? [];
    const groupsMap = new Map<string, GroupedNote>();

    for (const r of rows) {
      if (!r.noteId) {
        continue;
      }

      let group = groupsMap.get(r.noteId);
      if (!group) {
        group = {
          id: r.noteId,
          textContent: r.textContent ?? "",
          color: r.color ?? PRESET_COLORS[0]?.hex ?? "#eab308",
          createdAt: r.createdAt ?? new Date().toISOString(),
          tags: tagsByNote().get(r.noteId) ?? [],
          verses: [],
        };
        groupsMap.set(r.noteId, group);
      }

      if (r.verseId && r.book !== null && r.chapter !== null && r.verse !== null) {
        group.verses.push({
          id: r.verseId,
          version: r.version ?? "kjv",
          book: r.book,
          chapter: r.chapter,
          verse: r.verse,
        });
      }
    }

    const list = [...groupsMap.values()];

    for (const g of list) {
      g.verses.sort(compareVerseRefs);
    }

    // Filters (legacy parity: searchingInBooksFilter + bibleBookCategory) and
    // ordering (legacy parity: date | canon)
    const filtered = applyVerseGroupFilters(list, {
      b: bookFilter(),
      cat: categoryFilter(),
      tag: tagFilter(),
    });
    filtered.sort(orderParam() === "CANONICAL" ? compareByCanonicalOrder : compareByDate);

    return filtered;
  });

  // ── Export (kit K1) ─────────────────────────────────────────────────────────
  // The notes hub also exports the user's verse HIGHLIGHTS, so a single "Bible
  // annotations" file carries both kinds. Highlights live in a separate table
  // (`bible_verse_highlight`, grouped by `origin_id`); we only need their verse
  // coordinates + date for the reference/date columns.
  const highlightsForExportQuery = createPowerSyncQuery({
    queryKey: () => queryKeys.highlights.mine(undefined, session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({
          id: bible_verse_highlight.id,
          originId: bible_verse_highlight.origin_id,
          createdAt: bible_verse_highlight.created_at,
          version: bible_verse_highlight.version,
          book: bible_verse_highlight.book,
          chapter: bible_verse_highlight.chapter,
          verse: bible_verse_highlight.verse,
        })
        .from(bible_verse_highlight)
        .where(and(eq(bible_verse_highlight.user_id, u), isNull(bible_verse_highlight.deleted_at)));
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(session.userId()),
  });

  const groupedHighlightsForExport = createMemo(() => {
    const rows = highlightsForExportQuery.data ?? [];
    const groupsMap = new Map<
      string,
      {
        createdAt: string;
        tags: string[];
        verses: { version: string; book: number; chapter: number; verse: number }[];
      }
    >();
    for (const r of rows) {
      if (r.book === null || r.chapter === null || r.verse === null) {
        continue;
      }
      const key = r.originId ?? r.id;
      let group = groupsMap.get(key);
      if (!group) {
        group = { createdAt: r.createdAt ?? new Date().toISOString(), tags: [], verses: [] };
        groupsMap.set(key, group);
      }
      group.verses.push({
        version: r.version ?? "kjv",
        book: r.book,
        chapter: r.chapter,
        verse: r.verse,
      });
    }
    const list = [...groupsMap.values()];
    for (const g of list) {
      g.verses.sort(compareVerseRefs);
    }
    list.sort(compareByDate);
    return list;
  });

  /** Builds the combined notes+highlights dataset and downloads it as CSV/PDF. */
  const delivery = useKernelService(EXPORT_DELIVERY);

  const handleExport = (format: ExportFormat) => {
    const target = delivery();
    if (!target) {
      return;
    }
    const noteRows: BibleAnnotationExportRow[] = groupedNotes().map((n) => ({
      kind: BIBLE_ANNOTATION_KIND.note,
      reference: formatVerseReference(n.verses),
      text: n.textContent,
      createdAt: n.createdAt,
    }));
    const highlightRows: BibleAnnotationExportRow[] = groupedHighlightsForExport().map((h) => ({
      kind: BIBLE_ANNOTATION_KIND.highlight,
      reference: formatVerseReference(h.verses),
      text: "",
      createdAt: h.createdAt,
    }));
    const dataset = buildBibleAnnotationsDataset([...noteRows, ...highlightRows], {
      title: t(msg`Personal Notes`),
      subtitle: t(msg`Exported notes and highlights`),
    });
    void exportDataset(dataset, format, target);
  };

  const navigateWithSearch = (patch: Partial<NotesSearch>) => {
    void navigate({
      to: "/notes",
      search: {
        order: orderParam(),
        b: bookFilter(),
        cat: categoryFilter(),
        tag: tagFilter(),
        edit: editId(),
        compose: composeParam(),
        ...patch,
      },
    });
  };

  // The open editor is governed by the URL (`?edit=<id>` reopens an existing
  // note; `?compose=note` opens the creation editor). This makes the open form
  // **reopenable/shareable**: pasting/reloading the URL reproduces the drawer.
  const defaultColor = () => PRESET_COLORS[0]?.hex ?? "#eab308";

  // URL → editor state. Seeds the draft when opened; discards it when closed.
  // For an existing edit, we wait for the note to appear in the reactive list;
  // a nonexistent ID (another user / deleted) leaves the drawer closed.
  createEffect(() => {
    const id = editId();
    const compose = composeParam();
    if (id) {
      const note = groupedNotes().find((n) => n.id === id);
      // Only (re)seeds when the target note changes, not on every keystroke.
      if (note && editingNote()?.id !== note.id) {
        setEditingNote(note);
        setEditorText(note.textContent);
        setEditorColor(note.color);
        setEditorTags(note.tags.join(", "));
        setLinkedVerses([...note.verses]);
      }
      return;
    }
    if (compose === "note") {
      if (editingNote() === null || editingNote()?.id) {
        setEditingNote({
          id: "",
          textContent: "",
          color: defaultColor(),
          createdAt: new Date().toISOString(),
          tags: [],
          verses: [],
        });
        setEditorText("");
        setEditorColor(defaultColor());
        setEditorTags("");
        setLinkedVerses([]);
      }
      return;
    }
    if (editingNote() !== null) {
      setEditingNote(null);
    }
  });

  /** Opens the creation editor by writing `?compose=note`. */
  const handleCreateNew = () => {
    navigateWithSearch({ compose: "note", edit: undefined });
  };

  /** Opens an existing note's editor by writing `?edit=<id>`. */
  const handleEditNote = (note: GroupedNote) => {
    navigateWithSearch({ edit: note.id, compose: undefined });
  };

  /** Closes the drawer by clearing `?edit=`/`?compose=`. */
  const closeEditor = () => {
    navigateWithSearch({ edit: undefined, compose: undefined });
  };

  // Save changes locally in transaction
  // ── Undo helpers (L1) ───────────────────────────────────────────────────────
  type DrizzleDb = NonNullable<ReturnType<typeof drizzle>>;
  type NoteVerse = { version: string; book: number; chapter: number; verse: number };

  /** Common undo pill labels (localized once per registration). */
  const undoMsgs = (done: string) => ({
    done,
    undoLabel: t(msg`Undo`),
    undone: t(msg`Undone`),
    failed: t(msg`Couldn't undo`),
  });

  /** Hard-deletes a note and its children (inverse of "create"). */
  const hardDeleteNote = async (dbInstance: DrizzleDb, id: string) => {
    await dbInstance.delete(bible_note_tag).where(eq(bible_note_tag.note_id, id));
    await dbInstance.delete(bible_note_verse).where(eq(bible_note_verse.note_id, id));
    await dbInstance.delete(bible_note).where(eq(bible_note.id, id));
  };

  /** Restores a soft-deleted note (inverse of "move to trash"). */
  const restoreNote = async (dbInstance: DrizzleDb, id: string) => {
    await restoreNoteMutation(dbInstance, id);
  };

  /** Overwrites a note's content + verses + tags (inverse of "edit"). */
  const writeNoteContent = async (
    dbInstance: DrizzleDb,
    id: string,
    text: string,
    color: string,
    verses: readonly NoteVerse[],
    tags: readonly string[],
  ) => {
    await updateNoteContentMutation(dbInstance, id, text, color);
    await dbInstance.delete(bible_note_verse).where(eq(bible_note_verse.note_id, id));
    for (const v of verses) {
      // oxlint-disable-next-line no-await-in-loop
      await dbInstance.insert(bible_note_verse).values({
        id: generateId(),
        note_id: id,
        version: v.version,
        book: v.book,
        chapter: v.chapter,
        verse: v.verse,
      });
    }
    await dbInstance.delete(bible_note_tag).where(eq(bible_note_tag.note_id, id));
    for (const tag of tags) {
      // oxlint-disable-next-line no-await-in-loop
      await dbInstance.insert(bible_note_tag).values({
        id: generateId(),
        note_id: id,
        tag,
      });
    }
  };

  const handleSave = async () => {
    const dbInstance = drizzle();
    const u = session.userId();
    const note = editingNote();
    if (!dbInstance || !u || !note) {
      return;
    }

    // Snapshot the PRE-edit content so the undo of an edit can restore it.
    const wasNew = !note.id;
    const prevText = note.textContent;
    const prevColor = note.color;
    const prevVerses: NoteVerse[] = note.verses.map((v) => ({
      version: v.version,
      book: v.book,
      chapter: v.chapter,
      verse: v.verse,
    }));
    const prevTags = [...note.tags];

    try {
      const activeId = note.id || generateId();
      const now = new Date().toISOString();

      if (note.id) {
        // Update existing note
        await updateNoteContentMutation(dbInstance, note.id, editorText(), editorColor());

        // Delete existing verse relationships
        await dbInstance.delete(bible_note_verse).where(eq(bible_note_verse.note_id, note.id));
      } else {
        // Insert new note
        await dbInstance.insert(bible_note).values({
          id: activeId,
          user_id: u,
          text_content: editorText(),
          color: editorColor(),
          created_at: now,
          updated_at: now,
        });
      }

      // Re-insert linked verses
      const currentVerses = linkedVerses();
      if (currentVerses.length > 0) {
        const insertPromises = currentVerses.map((v) =>
          dbInstance.insert(bible_note_verse).values({
            id: generateId(),
            note_id: activeId,
            version: v.version,
            book: v.book,
            chapter: v.chapter,
            verse: v.verse,
          }),
        );
        await Promise.all(insertPromises);
      }

      // Replace tags with the edited list
      if (note.id) {
        await dbInstance.delete(bible_note_tag).where(eq(bible_note_tag.note_id, note.id));
      }
      const tagsList = editorTags()
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
      for (const tag of tagsList) {
        // oxlint-disable-next-line no-await-in-loop
        await dbInstance.insert(bible_note_tag).values({
          id: generateId(),
          note_id: activeId,
          tag,
        });
      }

      // Register undo only after the write succeeded (L1).
      if (wasNew) {
        registerUndo(undoMsgs(t(msg`Note created`)), () => hardDeleteNote(dbInstance, activeId));
      } else {
        registerUndo(undoMsgs(t(msg`Note updated`)), () =>
          writeNoteContent(dbInstance, activeId, prevText, prevColor, prevVerses, prevTags),
        );
      }

      closeEditor();
    } catch (e) {
      console.error("Failed to save note:", e);
    }
  };

  // Logical delete of note
  const handleDelete = async (noteId: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }

    try {
      await softDeleteNoteMutation(dbInstance, noteId);

      // Soft-delete → undo restores it (L1); the note also lives in the trash (L2).
      registerUndo(undoMsgs(t(msg`Note moved to trash`)), () => restoreNote(dbInstance, noteId));

      if (editingNote()?.id === noteId) {
        closeEditor();
      }
    } catch (e) {
      console.error("Failed to delete note:", e);
    }
  };

  // Link a verse in the editor
  const handleLinkVerse = () => {
    const exists = linkedVerses().some(
      (v) => v.book === pickerBook() && v.chapter === pickerChapter() && v.verse === pickerVerse(),
    );
    if (exists) {
      return;
    }

    setLinkedVerses((prev) => [
      ...prev,
      {
        version: "kjv",
        book: pickerBook(),
        chapter: pickerChapter(),
        verse: pickerVerse(),
      },
    ]);
  };

  // Unlink a verse in the editor
  const handleUnlinkVerse = (index: number) => {
    setLinkedVerses((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div class="flex flex-col h-full w-full bg-background text-foreground overflow-hidden">
      {/* Header */}
      <div class="border-b border-border/40 bg-card/30 backdrop-blur-lg px-4 md:px-8 py-4 md:py-5 flex items-center justify-between shrink-0">
        <div class="flex items-center gap-3">
          <div class="p-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
            <FileText class="h-5 w-5" />
          </div>
          <div>
            <h1 class="text-xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/75 bg-clip-text text-transparent">
              {t(msg`Personal Notes`)}
            </h1>
            <p class="text-xs text-muted-foreground mt-0.5">
              {t(msg`Your biblical studies and reflections`)}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <Popover>
            <PopoverTrigger
              as={Button}
              variant="outline"
              size="sm"
              class="h-9 gap-1.5 text-xs rounded-xl hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none focus:scale-100 focus:brightness-100"
            >
              <Download class="h-3.5 w-3.5" />
              {t(msg`Export`)}
            </PopoverTrigger>
            <PopoverContent class="w-44 p-2">
              <div class="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  class="justify-start h-8 text-xs"
                  onClick={() => handleExport("csv")}
                >
                  {t(msg`Export CSV`)}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  class="justify-start h-8 text-xs"
                  onClick={() => handleExport("pdf")}
                >
                  {t(msg`Export PDF`)}
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <button
            type="button"
            onClick={handleCreateNew}
            disabled={!drizzle() || !session.userId()}
            class="px-4 py-2.5 bg-primary text-primary-foreground hover:bg-primary/95 text-xs font-bold rounded-xl flex items-center gap-2 shadow-md hover:shadow-lg active:scale-98 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus class="w-4 h-4" />
            <span>{t(msg`Create Note`)}</span>
          </button>
        </div>
      </div>

      {/* Control bar: ordering + filters (legacy parity: notes/filters/*) */}
      <div class="border-b border-border/20 px-4 md:px-8 py-3 md:py-4 bg-muted/20 flex flex-wrap gap-3 md:gap-4 items-center justify-between shrink-0">
        <div class="flex items-center gap-1.5 bg-background border border-border/40 p-1 rounded-xl text-xs font-semibold shadow-2xs">
          <button
            type="button"
            onClick={() => {
              navigateWithSearch({ order: "DATE" });
            }}
            class="px-3.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
            classList={{
              "bg-primary text-primary-foreground": orderParam() === "DATE",
              "hover:bg-muted text-muted-foreground": orderParam() !== "DATE",
            }}
          >
            <Calendar class="w-3.5 h-3.5" />
            <span>{t(msg`Date`)}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              navigateWithSearch({ order: "CANONICAL" });
            }}
            class="px-3.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
            classList={{
              "bg-primary text-primary-foreground": orderParam() === "CANONICAL",
              "hover:bg-muted text-muted-foreground": orderParam() !== "CANONICAL",
            }}
          >
            <BookOpen class="w-3.5 h-3.5" />
            <span>{t(msg`Biblical Order`)}</span>
          </button>
        </div>

        <div class="flex items-center gap-3">
          {/* Canonical category filter */}
          <div class="relative">
            <button
              type="button"
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown())}
              class="px-4 py-2 bg-background border border-border/40 hover:bg-muted/40 text-xs font-bold rounded-xl flex items-center gap-2 transition-all shadow-2xs"
            >
              <Layers class="w-3.5 h-3.5 text-muted-foreground" />
              <span>{categoryFilter() ?? t(msg`All the Bible`)}</span>
              <ChevronDown class="w-3.5 h-3.5 text-muted-foreground" />
            </button>

            <ResponsiveSheet
              open={showCategoryDropdown()}
              onClose={() => setShowCategoryDropdown(false)}
              title={t(msg`Canonical category`)}
              dropdownClass="w-60"
            >
              {() => (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      navigateWithSearch({ cat: undefined });
                      setShowCategoryDropdown(false);
                    }}
                    class="w-full text-left px-3 py-2.5 md:py-1.5 text-sm md:text-xs rounded-lg hover:bg-muted/40 font-medium transition-colors"
                    classList={{
                      "bg-primary/10 text-primary font-semibold": !categoryFilter(),
                      "text-foreground/80": Boolean(categoryFilter()),
                    }}
                  >
                    {t(msg`All the Bible`)}
                  </button>
                  <For each={bookGroups}>
                    {(group) => (
                      <button
                        type="button"
                        onClick={() => {
                          navigateWithSearch({ cat: group.key });
                          setShowCategoryDropdown(false);
                        }}
                        class="w-full text-left px-3 py-2.5 md:py-1.5 text-sm md:text-xs rounded-lg hover:bg-muted/40 font-medium transition-colors"
                        classList={{
                          "bg-primary/10 text-primary font-semibold":
                            categoryFilter() === group.key,
                          "text-foreground/80": categoryFilter() !== group.key,
                        }}
                      >
                        {t(group.name)}
                      </button>
                    )}
                  </For>
                </>
              )}
            </ResponsiveSheet>
          </div>

          {/* Book filter */}
          <div class="relative">
            <button
              type="button"
              onClick={() => setShowBookDropdown(!showBookDropdown())}
              class="px-4 py-2 bg-background border border-border/40 hover:bg-muted/40 text-xs font-bold rounded-xl flex items-center gap-2 transition-all shadow-2xs"
            >
              <Filter class="w-3.5 h-3.5 text-muted-foreground" />
              <span>
                {(() => {
                  const b = bookFilter();
                  return b === undefined ? t(msg`Filter by book`) : t(getBookNameDescriptor(b));
                })()}
              </span>
              <ChevronDown class="w-3.5 h-3.5 text-muted-foreground" />
            </button>

            <ResponsiveSheet
              open={showBookDropdown()}
              onClose={() => setShowBookDropdown(false)}
              title={t(msg`Filter by Book`)}
            >
              {() => (
                <>
                  <Show when={bookFilter()}>
                    <div class="flex items-center justify-end border-b border-border/20 pb-2 mb-2 px-2">
                      <button
                        type="button"
                        onClick={() => {
                          navigateWithSearch({ b: undefined });
                          setShowBookDropdown(false);
                        }}
                        class="text-3xs text-primary font-bold hover:underline flex items-center gap-0.5"
                      >
                        <X class="w-3 h-3" />
                        <span>{t(msg`View all`)}</span>
                      </button>
                    </div>
                  </Show>
                  <div class="space-y-0.5">
                    <For each={bibleBooks}>
                      {(book) => (
                        <button
                          type="button"
                          onClick={() => {
                            navigateWithSearch({ b: book.id });
                            setShowBookDropdown(false);
                          }}
                          class="w-full text-left px-3 py-2.5 md:py-1.5 text-sm md:text-xs rounded-lg hover:bg-muted/40 font-medium transition-colors"
                          classList={{
                            "bg-primary/10 text-primary font-semibold": bookFilter() === book.id,
                            "text-foreground/80": bookFilter() !== book.id,
                          }}
                        >
                          {t(book.name)}
                        </button>
                      )}
                    </For>
                  </div>
                </>
              )}
            </ResponsiveSheet>
          </div>
        </div>
      </div>

      {/* Tag filter chips */}
      <Show when={allTags().length > 0}>
        <div class="border-b border-border/20 px-4 md:px-8 py-3 bg-muted/10 flex flex-wrap items-center gap-2 shrink-0">
          <span class="text-3xs font-bold text-muted-foreground uppercase flex items-center gap-1.5">
            <Tag class="w-3 h-3" />
            {t(msg`Tags:`)}
          </span>
          <For each={allTags()}>
            {(tag) => (
              <button
                type="button"
                onClick={() => {
                  navigateWithSearch({ tag: tagFilter() === tag ? undefined : tag });
                }}
                class="px-2.5 py-1 text-3xs font-bold border rounded-lg transition-all"
                classList={{
                  "bg-primary text-primary-foreground border-primary shadow-xs":
                    tagFilter() === tag,
                  "bg-background hover:bg-muted/40 border-border/40 text-foreground/80":
                    tagFilter() !== tag,
                }}
              >
                #{tag}
              </button>
            )}
          </For>
        </div>
      </Show>

      <div class="flex-1 flex overflow-hidden relative">
        {/* Notes Grid List */}
        <div class="flex-1 overflow-y-auto p-4 md:p-8">
          <Show
            when={groupedNotes().length > 0}
            fallback={
              <div class="max-w-md mx-auto py-20 text-center border border-dashed border-border/30 bg-card/10 rounded-2xl p-6 motion-safe:animate-fade-in">
                <EmptyIllustration variant="notes" class="mb-3 inline-flex" />
                <h3 class="text-sm font-bold text-foreground/85">{t(msg`No saved notes`)}</h3>
                <p class="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">
                  {t(
                    msg`Create your first personal study note to start documenting your learning.`,
                  )}
                </p>
              </div>
            }
          >
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pb-24">
              <For each={groupedNotes()}>
                {(note) => (
                  <div
                    role="button"
                    tabIndex={0}
                    data-testid="note-card"
                    data-note-text={note.textContent}
                    onClick={() => {
                      handleEditNote(note);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleEditNote(note);
                      }
                    }}
                    class="group relative flex flex-col bg-card/30 border border-border/35 hover:border-primary/30 rounded-2xl overflow-hidden shadow-2xs hover:shadow-xs transition-all cursor-pointer"
                  >
                    {/* Left Accent indicator */}
                    <div
                      class="absolute left-0 top-0 bottom-0 w-1.5"
                      style={{ "background-color": note.color }}
                    />

                    <div class="p-5 pl-7 flex flex-col justify-between flex-1 space-y-4">
                      <div class="space-y-3">
                        <div class="flex items-center justify-between">
                          <span class="text-3xs text-muted-foreground font-mono flex items-center gap-1.5">
                            <Calendar class="w-3 h-3" />
                            {new Date(note.createdAt).toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                        </div>

                        {/* Title reference if verses linked */}
                        <Show when={note.verses.length > 0}>
                          <div class="flex items-center gap-1.5 text-xs font-bold text-primary font-serif">
                            <BookOpen class="w-3.5 h-3.5" />
                            <span>{formatVerseReference(note.verses)}</span>
                          </div>
                        </Show>

                        <p class="text-xs leading-relaxed text-foreground/80 line-clamp-4 font-sans select-none">
                          {note.textContent}
                        </p>

                        <Show when={note.tags.length > 0}>
                          <div class="flex flex-wrap gap-1.5">
                            <For each={note.tags}>
                              {(tag) => (
                                <span class="px-2 py-0.5 bg-muted/40 border border-border/30 text-3xs font-semibold rounded-md text-foreground/70">
                                  #{tag}
                                </span>
                              )}
                            </For>
                          </div>
                        </Show>
                      </div>

                      {/* Footer Actions */}
                      <div class="flex items-center justify-end border-t border-border/20 pt-3">
                        <button
                          type="button"
                          data-testid="note-delete"
                          disabled={!drizzle()}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDelete(note.id);
                          }}
                          class="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={t(msg`Delete Note`)}
                        >
                          <Trash2 class="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </div>

        {/* Sliding Editor Panel */}
        <Show when={editingNote()}>
          <div class="absolute inset-0 bg-background/40 backdrop-blur-xs z-40 md:relative md:inset-auto md:bg-transparent md:backdrop-blur-none shrink-0 flex justify-end">
            <div class="w-full sm:w-110 h-full bg-card border-l border-border/50 flex flex-col shadow-2xl z-50 animate-in slide-in-from-right duration-200">
              {/* Drawer Header */}
              <div class="p-5 border-b border-border/30 flex items-center justify-between">
                <div class="flex items-center gap-2.5">
                  <Edit3 class="w-5 h-5 text-primary" />
                  <h3 class="text-sm font-bold">
                    {editingNote()?.id ? t(msg`Edit Note`) : t(msg`New Note`)}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => closeEditor()}
                  class="p-1.5 hover:bg-muted text-muted-foreground rounded-lg"
                >
                  <X class="w-4.5 h-4.5" />
                </button>
              </div>

              {/* Drawer Content */}
              <div class="flex-1 overflow-y-auto p-5 space-y-6">
                {/* Note text */}
                <div class="space-y-2">
                  <label
                    for="note-content"
                    class="text-2xs font-bold text-muted-foreground uppercase tracking-wide"
                  >
                    {t(msg`Content`)}
                  </label>
                  <textarea
                    id="note-content"
                    rows="6"
                    placeholder={t(
                      msg`Write your reflections, sermon notes or study thoughts here...`,
                    )}
                    value={editorText()}
                    onInput={(e) => setEditorText(e.target.value)}
                    class="w-full p-4 bg-background border border-border/50 rounded-xl text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/25 transition-all font-sans"
                  />
                </div>

                {/* Color picker */}
                <div class="space-y-2">
                  <span class="text-2xs font-bold text-muted-foreground uppercase tracking-wide">
                    {t(msg`Label Color`)}
                  </span>
                  <div class="flex gap-2">
                    <For each={PRESET_COLORS}>
                      {(color) => (
                        <button
                          type="button"
                          onClick={() => setEditorColor(color.hex)}
                          class="w-8 h-8 rounded-full border border-border/40 flex items-center justify-center transition-all scale-100 hover:scale-105 active:scale-95 shadow-2xs"
                          style={{ "background-color": color.hex }}
                          title={t(color.name)}
                        >
                          <Show when={editorColor() === color.hex}>
                            <Check class="w-4 h-4 text-white drop-shadow-md" />
                          </Show>
                        </button>
                      )}
                    </For>
                  </div>
                </div>

                {/* Tags editor */}
                <div class="space-y-2">
                  <label
                    for="note-tags-input"
                    class="text-2xs font-bold text-muted-foreground uppercase tracking-wide"
                  >
                    {t(msg`Tags`)}
                  </label>
                  <div class="relative">
                    <Tag class="w-3.5 h-3.5 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      id="note-tags-input"
                      type="text"
                      value={editorTags()}
                      onInput={(e) => setEditorTags(e.currentTarget.value)}
                      placeholder={t(msg`Comma-separated tags`)}
                      class="w-full bg-background border border-border/50 rounded-xl pl-9 pr-4 py-2.5 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/25 transition-all"
                    />
                  </div>
                </div>

                {/* Linked Verses Manager */}
                <div class="space-y-4 border-t border-border/20 pt-4">
                  <div class="space-y-1">
                    <span class="text-2xs font-bold text-muted-foreground uppercase tracking-wide">
                      {t(msg`Linked Verses`)}
                    </span>
                    <p class="text-3xs text-muted-foreground">
                      {t(msg`Link this note to specific Bible verses.`)}
                    </p>
                  </div>

                  {/* List of currently linked verses */}
                  <Show
                    when={linkedVerses().length > 0}
                    fallback={
                      <p class="text-xs text-muted-foreground bg-muted/20 border border-border/10 p-3 rounded-lg text-center">
                        {t(msg`This note is not linked to any verse yet.`)}
                      </p>
                    }
                  >
                    <div class="flex flex-wrap gap-2">
                      <For each={linkedVerses()}>
                        {(v, index) => (
                          <div class="px-2.5 py-1 bg-primary/10 border border-primary/25 text-primary text-xs font-semibold rounded-lg flex items-center gap-1.5 font-serif">
                            <span>
                              {t(getBookNameDescriptor(v.book))} {v.chapter}:{v.verse}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                handleUnlinkVerse(index());
                              }}
                              class="p-0.5 hover:bg-primary/20 text-primary rounded-full transition-colors"
                            >
                              <X class="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </For>
                    </div>
                  </Show>

                  {/* Picker to link new verse */}
                  <div class="bg-muted/30 border border-border/40 p-4 rounded-xl space-y-3">
                    <div class="flex items-center justify-between border-b border-border/20 pb-1 mb-1">
                      <span class="text-3xs text-muted-foreground font-bold uppercase">
                        {t(msg`Link Verse`)}
                      </span>
                    </div>

                    <div class="grid grid-cols-3 gap-2">
                      {/* Book select */}
                      <div>
                        <label
                          for="link-book-select"
                          class="text-4xs text-muted-foreground font-bold uppercase"
                        >
                          {t(msg`Book`)}
                        </label>
                        <div class="mt-1">
                          <Select
                            id="link-book-select"
                            aria-label={t(msg`Book`)}
                            class="w-full"
                            value={pickerBook()}
                            onChange={(value) => setPickerBook(value)}
                            options={bibleBooks.map((b) => ({
                              value: b.id,
                              label: t(b.name),
                              textValue: t(b.name),
                            }))}
                          />
                        </div>
                      </div>

                      {/* Chapter input */}
                      <div>
                        <label
                          for="link-chapter-input"
                          class="text-4xs text-muted-foreground font-bold uppercase"
                        >
                          {t(msg`Chapter`)}
                        </label>
                        <input
                          id="link-chapter-input"
                          type="number"
                          min="1"
                          value={pickerChapter()}
                          onInput={(e) => setPickerChapter(Number(e.target.value))}
                          class="w-full mt-1 bg-background border border-border/40 rounded-lg p-1.5 text-xs text-foreground/90 focus:outline-none"
                        />
                      </div>

                      {/* Verse input */}
                      <div>
                        <label
                          for="link-verse-input"
                          class="text-4xs text-muted-foreground font-bold uppercase"
                        >
                          {t(msg`Verse`)}
                        </label>
                        <input
                          id="link-verse-input"
                          type="number"
                          min="1"
                          value={pickerVerse()}
                          onInput={(e) => setPickerVerse(Number(e.target.value))}
                          class="w-full mt-1 bg-background border border-border/40 rounded-lg p-1.5 text-xs text-foreground/90 focus:outline-none"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleLinkVerse}
                      class="w-full py-2 bg-muted hover:bg-muted/80 text-foreground font-semibold text-xs rounded-lg border border-border/30 transition-all active:scale-98"
                    >
                      {t(msg`Add to list`)}
                    </button>
                  </div>
                </div>
              </div>

              {/* Drawer Footer Actions */}
              <div class="p-5 border-t border-border/30 bg-muted/10 flex items-center justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => closeEditor()}
                  class="px-4 py-2 border border-border/50 hover:bg-muted text-xs font-semibold rounded-lg transition-colors"
                >
                  {t(msg`Cancel`)}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!editorText().trim() || !drizzle() || !session.userId()}
                  class="px-5 py-2 bg-primary hover:bg-primary/95 text-primary-foreground disabled:opacity-50 text-xs font-semibold rounded-lg shadow-md active:scale-98 transition-all"
                >
                  {t(msg`Save`)}
                </button>
              </div>
            </div>
          </div>
        </Show>
      </div>
    </div>
  );
}
